/* iter55l — Multi-wallet top-up hub.
 *
 * Three tabs, one entry point (File → Buy Tokens…):
 *   1. Claude / AI    → users.tokens_purchased
 *   2. Canva credits  → users.canva_credit_cents  (measured in ¢)
 *   3. Copyscape scans → users.copyscape_tokens
 *
 * The header ALWAYS shows all three balances so the user knows what
 * they currently own before choosing what to top up. Each tab routes
 * to its own Stripe Checkout endpoint (routes_payments for AI +
 * Copyscape, routes_canva for Canva).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';   // Feb-2026 · i18n Wave 5
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Wallet, Zap, Loader2, KeyRound, Image as ImageIcon, Search, Sparkles, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { paymentsApi } from '@/lib/auth_api';
import { useAuth } from '@/lib/auth_context';
import { usePaymentsStatus, logDowntimeClick } from '@/lib/payments_status';
import { PackagePicker } from './tokens/PackagePicker';
import { CustomAmountRow } from './tokens/CustomAmountRow';

// ---- 3-balance header ---------------------------------------------

function BalanceHeader({ user }) {
  const { t } = useTranslation();
  const ai   = user?.tokens_purchased || 0;
  const cs   = user?.copyscape_tokens || 0;
  // iter55z-g — Canva pivoted to BYO. Any lingering canva_credit_cents is
  // now a legacy balance for historical bookkeeping; the header shows the
  // Canva connection status instead of a dollar figure.
  const canvaConnected = !!user?.canva?.connected;

  return (
    <div className="grid grid-cols-3 gap-3" data-testid="buy-tokens-balances">
      <BalanceCard icon={Sparkles} colour="text-amber-500"
                    label={t('tokens.aiTokens')} value={ai.toLocaleString()}
                    sub={t('tokens.aiTokensSub')}
                    testId="balance-claude" />
      <BalanceCard icon={ImageIcon} colour="text-fuchsia-500"
                    label={t('tokens.canvaCredits')} value={canvaConnected ? t('tokens.canvaLinked') : t('tokens.canvaNotLinked')}
                    sub={canvaConnected ? t('tokens.canvaLinkedSub') : t('tokens.canvaNotLinkedSub')}
                    testId="balance-canva" />
      <BalanceCard icon={Search} colour="text-primary"
                    label={t('tokens.copyscapeScans')} value={cs.toLocaleString()}
                    sub={t('tokens.copyscapeSub')}
                    testId="balance-copyscape" />
    </div>
  );
}

function BalanceCard({ icon: Icon, colour, label, value, sub, testId }) {
  return (
    <div className="rounded-md border border-border bg-card p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        <Icon className={`h-3 w-3 ${colour}`} /> {label}
      </div>
      <div className="mt-1 font-mono-stat text-[20px] font-semibold" data-testid={`${testId}-value`}>{value}</div>
      <div className="text-[10.5px] text-muted-foreground">{sub}</div>
    </div>
  );
}

// ---- Main dialog ---------------------------------------------------

export function BuyTokensDialog({ open, onOpenChange }) {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const payments = usePaymentsStatus();          // iter-payments-master
  const paymentsOff = payments.ready && !payments.enabled;
  const [tab, setTab] = useState('claude');
  const [packages, setPackages] = useState([]);
  const [custom, setCustom] = useState({ min_usd: 5, max_usd: 999, tokens_per_usd: 60000 });
  const [pick, setPick] = useState('pro');
  const [customAmount, setCustomAmount] = useState('');
  const [copyscapePacks, setCopyscapePacks] = useState([]);
  const [copyscapePick, setCopyscapePick] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    paymentsApi.packages()
      .then((r) => {
        setPackages(r.packages || []);
        if (r.custom) setCustom(r.custom);
        setCopyscapePacks(r.copyscape_packs || []);
        // Default to the "best" pack, else the first.
        const best = (r.copyscape_packs || []).find((p) => p.best);
        setCopyscapePick((best || r.copyscape_packs?.[0])?.id || null);
      })
      .catch(() => toast.error('Could not load pricing.'));
    refresh?.();
  }, [open, refresh]);

  const isCustom = pick === 'custom';
  const customNum = parseFloat(customAmount) || 0;
  const tokensForCustom = Math.floor(customNum * (custom.tokens_per_usd || 60000));

  const buyClaude = async () => {
    if (paymentsOff) {
      logDowntimeClick('buy-tokens', 'buy-claude-continue', { tierOrPack: isCustom ? 'custom' : pick, userId: user?.id });
      toast.error(payments.reason || 'Card processing is paused. Please try again shortly.');
      return;
    }
    setBusy(true);
    try {
      const origin = window.location.origin;
      const r = isCustom
        ? await paymentsApi.checkoutCustom(customNum, origin)
        : await paymentsApi.checkoutPackage(pick, origin);
      if (r?.url) window.location.href = r.url;
      else throw new Error('No checkout URL');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  const buyCopyscape = async () => {
    if (paymentsOff) {
      logDowntimeClick('buy-tokens', 'buy-copyscape-continue', { tierOrPack: copyscapePick, userId: user?.id });
      toast.error(payments.reason || 'Card processing is paused. Please try again shortly.');
      return;
    }
    if (!copyscapePick) return;
    setBusy(true);
    try {
      const r = await paymentsApi.startCopyscapePack(copyscapePick, window.location.origin);
      if (r?.url) window.location.href = r.url;
      else throw new Error('No checkout URL');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start checkout.');
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl overflow-hidden p-0" data-testid="buy-tokens-dialog">
        <DialogHeader className="border-b border-border bg-panel-header/40 px-5 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Wallet className="h-4 w-4 text-primary" /> {t('tokens.buyTitle')}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            {t('tokens.buyDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4">
          {paymentsOff && (
            <div
              className="flex items-start gap-2 rounded-md border-2 border-rose-500/60 bg-rose-50 px-3 py-2 text-[12px] text-rose-900"
              data-testid="buy-tokens-payments-off-notice"
            >
              <Ban className="mt-0.5 h-4 w-4 shrink-0 text-rose-700" />
              <div>
                <div className="font-semibold">Card processing is paused</div>
                <div className="text-[11px]">
                  {payments.reason || 'Purchases are temporarily disabled. Please try again shortly.'}
                </div>
              </div>
            </div>
          )}
          <BalanceHeader user={user} />

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-9 w-full grid grid-cols-3">
              <TabsTrigger value="claude"    className="text-[12px] gap-1.5" data-testid="buy-tab-claude">
                <Sparkles className="h-3.5 w-3.5 text-amber-500" /> {t('tokens.aiTokens')}
              </TabsTrigger>
              <TabsTrigger value="canva"     className="text-[12px] gap-1.5" data-testid="buy-tab-canva">
                <ImageIcon className="h-3.5 w-3.5 text-fuchsia-500" /> {t('tokens.canvaCredits')}
              </TabsTrigger>
              <TabsTrigger value="copyscape" className="text-[12px] gap-1.5" data-testid="buy-tab-copyscape">
                <Search className="h-3.5 w-3.5 text-primary" /> {t('tokens.copyscapeScans')}
              </TabsTrigger>
            </TabsList>

            {/* --- Claude / AI tab ---------------------------------------- */}
            <TabsContent value="claude" className="m-0 mt-3 space-y-3" data-testid="buy-content-claude">
              <TabIntro>
                Fuel <b>Draft-AI</b>, <b>Claude Chat</b>, and every scene-generation tool
                in the writing suite. Tokens never expire and roll from month to month.
              </TabIntro>
              <PackagePicker packages={packages} selectedId={pick} onSelect={setPick} />
              <CustomAmountRow
                config={custom}
                isCustom={isCustom}
                onSelect={() => setPick('custom')}
                amount={customAmount}
                onAmountChange={setCustomAmount}
                tokens={tokensForCustom}
              />
              <StripeKeyNote user={user} />
              <div className="flex justify-end">
                <Button
                  size="sm" onClick={buyClaude}
                  disabled={busy || paymentsOff || (isCustom && (!customNum || customNum < custom.min_usd))}
                  data-pay-button
                  className="gap-1.5" data-testid="buy-claude-continue">
                  {paymentsOff ? <Ban className="h-3.5 w-3.5" /> : busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  {paymentsOff ? 'Payments paused' : busy ? 'Redirecting…' : 'Continue to Stripe'}
                </Button>
              </div>
            </TabsContent>

            {/* --- Canva tab (iter55z-g: BYO pivot, wallet retired) ------- */}
            <TabsContent value="canva" className="m-0 mt-3 space-y-3" data-testid="buy-content-canva">
              <TabIntro>
                Canva credits have been replaced with <b>Bring Your Own Canva</b>.
                Link your personal Canva account once and every cover you design
                uses <i>your</i> Canva subscription and quota — no per-cover fee to us.
              </TabIntro>
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-[12px] text-sky-900" data-testid="canva-byo-notice">
                <p className="font-semibold">Connect your Canva account</p>
                <p className="mt-1 text-[11.5px] text-sky-900/80">
                  Open <b>Preferences → Connections</b> and click <b>Connect Canva Account</b>.
                  Once linked, the <i>Design a Cover with Canva</i> button in the File menu
                  will open a new blank cover in your own Canva workspace.
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { onOpenChange(false); window.dispatchEvent(new CustomEvent('pw:open-connections')); }}
                  className="gap-1.5"
                  data-testid="buy-canva-open-connections"
                >
                  Open Connections
                </Button>
              </div>
            </TabsContent>

            {/* --- Copyscape tab ------------------------------------------ */}
            <TabsContent value="copyscape" className="m-0 mt-3 space-y-3" data-testid="buy-content-copyscape">
              <TabIntro>
                Copyscape scans check any chapter or full manuscript against the web for
                plagiarism. Each scan spends <b>1 token</b> (or more if you enlarge the sample).
              </TabIntro>
              {copyscapePacks.length === 0 ? (
                <div className="rounded-md border border-dashed border-border p-4 text-center text-[11.5px] text-muted-foreground">
                  Copyscape packs aren&apos;t configured yet. Ask an admin to enable them.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2" data-testid="copyscape-pack-picker">
                  {copyscapePacks.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setCopyscapePick(p.id)}
                      data-testid={`copyscape-pack-${p.id}`}
                      data-active={copyscapePick === p.id ? '1' : '0'}
                      className={`rounded-md border p-3 text-left transition-colors ${
                        copyscapePick === p.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/60'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[11.5px] font-semibold">{p.name}</span>
                        {p.best && (
                          <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[9.5px] font-medium text-primary">
                            Best value
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[18px] font-mono-stat font-semibold">${p.price?.toFixed?.(2) ?? p.price}</div>
                      <div className="text-[10.5px] text-muted-foreground">{p.tokens} scans</div>
                    </button>
                  ))}
                </div>
              )}
              <StripeKeyNote user={user} />
              <div className="flex justify-end">
                <Button
                  size="sm" onClick={buyCopyscape}
                  disabled={busy || paymentsOff || !copyscapePick}
                  data-pay-button
                  className="gap-1.5" data-testid="buy-copyscape-continue">
                  {paymentsOff ? <Ban className="h-3.5 w-3.5" /> : busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                  {paymentsOff ? 'Payments paused' : busy ? 'Redirecting…' : 'Continue to Stripe'}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <DialogFooter className="border-t border-border bg-panel-header/40 px-5 py-3">
          <span className="mr-auto text-[10.5px] text-muted-foreground">
            You&apos;ll be redirected to Stripe Checkout to complete the purchase.
          </span>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}
                  data-testid="buy-tokens-cancel">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TabIntro({ children }) {
  return (
    <div className="rounded-md border border-border bg-secondary/40 px-3 py-2 text-[11.5px] text-muted-foreground">
      {children}
    </div>
  );
}

function StripeKeyNote({ user }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-panel-header/30 px-3 py-2 text-[10.5px] text-muted-foreground">
      <KeyRound className="h-3.5 w-3.5" />
      <span>
        {user?.has_personal_stripe_key
          ? 'Payments will be processed with your personal Stripe API key.'
          : 'Payments are processed via the Emergent test-mode Stripe key. To use real billing, add your own Stripe key in Settings → Billing.'}
      </span>
    </div>
  );
}
